import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  LayoutDashboard, CheckSquare, MessageSquare, Mail, MessageCircle,
  FileText, Settings, LogOut, Menu, X, Briefcase,
  CalendarDays, FileBarChart, Users, CreditCard, CalendarClock, Shield, Car,
  ChevronDown, PanelLeftClose, PanelLeft, Chrome, Mic, Landmark, Target, AlertTriangle, Tag, BarChart3, HelpCircle, Database, UserPlus,
  Building2, Wallet, FolderKanban, FileBarChart2, Bot
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_37034127-053c-4dea-b78b-0ae818821816/artifacts/vhfwrtom_2.png';

export const Sidebar = () => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [badgeInAttesa, setBadgeInAttesa] = useState(0);
  const [mini, setMini] = useState(() => {
    try { return localStorage.getItem('sidebar_mini') === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('sidebar_mini', mini ? '1' : '0'); } catch {}
  }, [mini]);

  // Fetch badge count for "Richieste Emissione"
  useEffect(() => {
    const fetchBadge = async () => {
      try {
        const res = await api.get('/richieste-emissione/badge/in-attesa');
        setBadgeInAttesa(res.data.count || 0);
      } catch {}
    };
    if (user?.ruolo !== 'collaboratore') {
      fetchBadge();
      const interval = setInterval(fetchBadge, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggle = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  const isCollaboratore = user?.ruolo === 'collaboratore';

  // Collaboratori vedono la sezione richieste + strumenti essenziali di lavoro
  const sections = isCollaboratore ? [
    {
      key: 'collab',
      label: 'Area Collaboratore',
      items: [
        { icon: LayoutDashboard, label: 'La Mia Dashboard', path: '/my-dashboard' },
        { icon: CalendarDays, label: 'Calendario', path: '/calendario' },
        { icon: FileText, label: 'Richieste Emissione', path: '/richieste-emissione' },
        { icon: CalendarClock, label: 'Scadenze', path: '/scadenze' },
        { icon: Landmark, label: 'Prestiti & CQ', path: '/prestiti' },
        { icon: Target, label: 'Lead Credito', path: '/leads?tipo=credito' },
        { icon: UserPlus, label: 'Nuova Richiesta', path: '/richieste-emissione?new=1' },
        { icon: HelpCircle, label: 'Guida', path: '/guida' },
      ],
    },
  ] : [
    {
      key: 'main',
      label: 'Principale',
      items: [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
        { icon: CalendarDays, label: 'Calendario', path: '/calendario' },
        { icon: CheckSquare, label: 'Task', path: '/tasks' },
      ],
    },
    {
      key: 'clients',
      label: 'Clienti & Polizze',
      items: [
        { icon: CalendarClock, label: 'Scadenze', path: '/scadenze' },
        { icon: Target, label: 'Cross-sell / Opportunità', path: '/consulenze' },
        { icon: Shield, label: 'Garanzie / Comparatore', path: '/garanzie' },
        { icon: Tag, label: 'Clienti', path: '/clienti' },
        { icon: Car, label: 'Preventivatore Auto', path: '/preventivatore-auto', badge: 'Prossimamente' },
        { icon: FileBarChart, label: 'Preventivi', path: '/preventivi' },
        { icon: CreditCard, label: 'Finanzia Polizza', path: '/finanzia-polizza' },
        { icon: AlertTriangle, label: 'Sinistri', path: '/sinistri' },
        { icon: FileText, label: 'Richieste Emissione', path: '/richieste-emissione' },
        { icon: BarChart3, label: 'Dashboard Collaboratori', path: '/dashboard-collaboratori' },
      ],
    },
    {
      key: 'credit',
      label: 'Credito & Finanziamenti',
      items: [
        { icon: Landmark, label: 'Prestiti & CQ', path: '/prestiti' },
        { icon: Target, label: 'Lead Credito', path: '/leads?tipo=credito' },
      ],
    },
    {
      key: 'agenzia',
      label: 'CRM Agenzia',
      items: [
        { icon: FolderKanban, label: 'Polizze', path: '/agenzia/polizze', badge: 'Prossimamente' },
        { icon: Building2, label: 'Clienti Agenzia', path: '/agenzia/clienti', badge: 'Prossimamente' },
        { icon: Wallet, label: 'Cassa', path: '/agenzia/cassa', badge: 'Prossimamente' },
        { icon: FileBarChart2, label: 'Report Agenzia', path: '/agenzia/report', badge: 'Prossimamente' },
        { icon: Building2, label: 'Compagnie Agenzia', path: '/agenzia/compagnie', badge: 'Prossimamente' },
      ],
    },
    {
      key: 'comm',
      label: 'Comunicazione',
      items: [
        { icon: MessageSquare, label: 'Chat Interna', path: '/chat' },
        { icon: Bot, label: 'Assistente AI', path: '/impostazioni-ai' },
        { icon: Mail, label: 'Email', path: '/emails' },
        { icon: MessageCircle, label: 'WhatsApp Ufficio', path: '/whatsapp' },
      ],
    },
    {
      key: 'mgmt',
      label: 'Gestione',
      items: [
        { icon: FileText, label: isAdmin ? 'Report Dipendenti' : 'Attivita\'', path: '/reports' },
        { icon: Briefcase, label: 'HR', path: '/hr' },
        { icon: Mic, label: 'Note AI', path: '/note-ai' },
        { icon: Chrome, label: 'Estensione Chrome', path: '/estensione-chrome' },
        ...(isAdmin ? [
          { icon: Users, label: 'Gestione Utenti', path: '/gestione-utenti' },
          { icon: Settings, label: 'Impostazioni', path: '/settings' },
        ] : []),
      ],
    },
  ];

  const isActive = (path) =>
    path === '/hr' ? location.pathname.startsWith('/hr') : location.pathname === path;

  const sectionHasActive = (items) => items.some(i => isActive(i.path));

  /* ─── Mini sidebar (solo icone) ─── */
  const MiniContent = () => (
    <TooltipProvider delayDuration={0}>
      <div className="flex items-center justify-center py-4 border-b border-slate-700/50">
        <img src={LOGO_URL} alt="Logo" className="h-8 w-8 object-contain rounded" />
      </div>

      <nav className="flex-1 py-2 px-1.5 overflow-y-auto space-y-0.5">
        {sections.flatMap(s => s.items).map((item) => {
          const active = isActive(item.path);
          return (
            <Tooltip key={item.path}>
              <TooltipTrigger asChild>
                <Link
                  to={item.path}
                  data-testid={`sidebar-mini-${item.path.replace('/', '')}`}
                  className={`flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-all ${
                    active
                      ? 'bg-amber-500/20 text-amber-400'
                      : 'text-slate-400 hover:bg-slate-700 hover:text-slate-200'
                  }`}
                >
                  <item.icon size={18} />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right" className="text-xs">
                {item.label}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </nav>

      <div className="p-1.5 border-t border-slate-700/50 space-y-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center justify-center">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-semibold text-xs">
                {user?.nome?.[0]}{user?.cognome?.[0]}
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-xs">{user?.nome} {user?.cognome}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button onClick={handleLogout} data-testid="logout-button-mini"
              className="flex items-center justify-center w-10 h-10 mx-auto text-slate-500 hover:bg-red-500/10 hover:text-red-400 rounded-lg transition-colors">
              <LogOut size={16} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-xs">Esci</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );

  /* ─── Full sidebar (espansa) ─── */
  const FullContent = () => (
    <>
      <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-700/50">
        <img src={LOGO_URL} alt="F.lli Schiliro'" className="h-10 w-auto object-contain" />
      </div>

      <nav className="flex-1 p-3 overflow-y-auto">
        {sections.map((section) => {
          const isOpen = collapsed[section.key] === undefined ? true : !collapsed[section.key];
          const hasActive = sectionHasActive(section.items);
          return (
            <div key={section.key} className="mb-1">
              <button
                onClick={() => toggle(section.key)}
                className="w-full flex items-center justify-between px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors"
                data-testid={`section-${section.key}`}
              >
                <span className={hasActive && !isOpen ? 'text-amber-400' : ''}>{section.label}</span>
                <ChevronDown size={12} className={`transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="space-y-0.5 pb-1">
                  {section.items.map((item) => {
                    const active = isActive(item.path);
                    return (
                      <Link key={item.path} to={item.badge ? '#' : item.path}
                        onClick={(e) => { if (item.badge) { e.preventDefault(); return; } setMobileOpen(false); }}
                        data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-').replace(/['']/g, '')}`}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-[13px] ${
                          item.badge ? 'text-slate-600 cursor-default' :
                          active ? 'bg-amber-500/15 text-amber-400 font-medium' : 'text-slate-300 hover:bg-slate-700/60 hover:text-white'
                        }`}
                      >
                        <item.icon size={16} />
                        <span>{item.label}</span>
                        {item.badge && <span className="ml-auto text-[9px] bg-slate-700 text-slate-400 px-1.5 py-0.5 rounded-full font-medium">{item.badge}</span>}
                        {item.path === '/richieste-emissione' && badgeInAttesa > 0 && (
                          <span className="ml-auto bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1">{badgeInAttesa}</span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </nav>

      <div className="p-3 border-t border-slate-700/50">
        <div className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-700/40 mb-2">
          <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-semibold text-xs flex-shrink-0">
            {user?.nome?.[0]}{user?.cognome?.[0]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-200 truncate">{user?.nome} {user?.cognome}</p>
            <p className="text-[10px] text-slate-500">{user?.ruolo === 'super_admin' ? 'Admin' : user?.ruolo === 'collaboratore' ? 'Collaboratore' : 'Dipendente'}</p>
          </div>
        </div>
        <button onClick={handleLogout} data-testid="logout-button"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:bg-red-500/10 hover:text-red-400 rounded-lg transition-colors">
          <LogOut size={16} /><span>Esci</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button onClick={() => setMobileOpen(!mobileOpen)}
        className="md:hidden fixed top-[calc(0.75rem+env(safe-area-inset-top))] left-3 z-50 p-2 bg-white rounded-lg shadow-lg border border-slate-200"
        data-testid="mobile-menu-button">
        {mobileOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar container */}
      <aside
        className={`
          fixed md:sticky top-0 left-0 h-[100dvh] bg-slate-800
          flex flex-col z-40 transition-all duration-300 ease-in-out
          ${mini ? 'md:w-[52px]' : 'md:w-60'}
          w-60
          md:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="sidebar"
      >
        {/* Desktop: show mini or full. Mobile: always full */}
        <div className="hidden md:flex flex-col flex-1 overflow-hidden">
          {mini ? <MiniContent /> : <FullContent />}
        </div>
        <div className="md:hidden flex flex-col flex-1 overflow-hidden">
          <FullContent />
        </div>

        {/* Desktop toggle button */}
        <button
          onClick={() => setMini(!mini)}
          className="hidden md:flex items-center justify-center h-8 border-t border-slate-700/50 text-slate-500 hover:text-slate-300 hover:bg-slate-700/40 transition-colors"
          data-testid="sidebar-toggle"
          title={mini ? 'Espandi sidebar' : 'Comprimi sidebar'}
        >
          {mini ? <PanelLeft size={14} /> : <PanelLeftClose size={14} />}
        </button>
      </aside>
    </>
  );
};
